import React, { useState } from 'react';
import { 
  TrendingUp, 
  Calculator, 
  ArrowRightLeft, 
  Landmark, 
  Sparkles, 
  Receipt,
  ArrowRight,
  CheckCircle2,
  RotateCcw,
  Sparkle
} from 'lucide-react';
import { ToolCategory, ToolItem, RegionInfo } from '../../types';

interface ToolDiscoveryWizardProps {
  onSelectTool: (toolId: string) => void;
  tools: ToolItem[];
  allRegions: RegionInfo[];
  selectedRegion: RegionInfo;
}

export const ToolDiscoveryWizard: React.FC<ToolDiscoveryWizardProps> = ({
  onSelectTool,
  tools,
  allRegions,
  selectedRegion,
}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedIntent, setSelectedIntent] = useState<ToolCategory | null>(null);
  const [targetGoal, setTargetGoal] = useState<string | null>(null);

  const intents: {
    category: ToolCategory;
    title: string;
    description: string;
    icon: React.ReactNode;
    goals: { label: string; toolId: string; subtitle: string }[];
  }[] = [
    {
      category: 'Everyday Money',
      title: 'Everyday taxes, tips, discounts & bills',
      description: 'Quick consumer and business math for percentage, discounts, margin, markup, tip, VAT/GST & timesheets.',
      icon: <Receipt className="w-5 h-5" />,
      goals: [
        { label: 'Calculate Percentage & Proportions', toolId: 'percentage-calculator', subtitle: 'Instant % increase, decrease & proportions' },
        { label: 'Calculate Sales Discount & Final Price', toolId: 'discount-calculator', subtitle: 'Sale savings and final checkout price' },
        { label: 'Calculate VAT, TVA, GST & Sales Tax', toolId: 'vat-sales-tax', subtitle: 'Add or extract tax with reverse calculations' },
        { label: 'Calculate Restaurant Tip & Gratuities', toolId: 'tip-calculator', subtitle: 'Fair hospitality tips & split bills' },
      ],
    },
    {
      category: 'Income & Tax',
      title: 'Understand my salary, payroll & taxes',
      description: 'Calculate take-home pay, progressive tax brackets, and true cost of employment across 50+ countries.',
      icon: <Calculator className="w-5 h-5" />,
      goals: [
        { label: 'Estimate monthly take-home salary after tax', toolId: 'salary-calculator', subtitle: '50+ country tax engines & brackets' },
        { label: 'Gross to Net paycheck breakdown', toolId: 'gross-to-net-calculator', subtitle: 'Statutory withholdings & take-home pay' },
        { label: 'Explore progressive tax brackets & marginal rates', toolId: 'income-tax-calculator', subtitle: 'Marginal vs effective tax rates' },
      ],
    },
    {
      category: 'Currency & Transfers',
      title: 'Convert money & compare international transfers',
      description: 'Find real-time institutional FX rates and uncover hidden provider markups across 170+ currencies.',
      icon: <TrendingUp className="w-5 h-5" />,
      goals: [
        { label: 'Convert between two currencies at mid-market rate', toolId: 'currency-converter', subtitle: 'Institutional wholesale mid-market rate' },
        { label: 'Compare transfer provider fees & FX spreads', toolId: 'compare-transfers', subtitle: 'Wise, OFX, Remitly & bank costs' },
        { label: 'View multi-currency cross-rate matrix', toolId: 'exchange-rates-matrix', subtitle: 'Track major reserve pairs simultaneously' },
      ],
    },
    {
      category: 'Borrow',
      title: 'Calculate loans, APR & mortgage payments',
      description: 'Simulate monthly amortization, interest costs, and early payoff strategies.',
      icon: <Landmark className="w-5 h-5" />,
      goals: [
        { label: 'Calculate monthly mortgage payment (P&I)', toolId: 'mortgage-calculator', subtitle: 'Principal, interest, and payoff schedule' },
        { label: 'Personal & Auto Loan installments', toolId: 'loan-calculator', subtitle: 'Fixed installments and total interest' },
        { label: 'Simulate early extra payment savings', toolId: 'early-repayment-calculator', subtitle: 'Years shaved off and interest saved' },
      ],
    },
    {
      category: 'Save & Invest',
      title: 'Grow savings, compound interest & retirement',
      description: 'Project compound interest portfolios, inflation protection, and retirement nest eggs.',
      icon: <Sparkles className="w-5 h-5" />,
      goals: [
        { label: 'Project compound interest with recurring deposits', toolId: 'compound-interest-calculator', subtitle: 'Future portfolio growth over 5-40 years' },
        { label: 'Calculate inflation impact on buying power', toolId: 'inflation-purchasing-power', subtitle: 'Real purchasing loss projections' },
        { label: 'Calculate Retirement & FIRE numbers', toolId: 'retirement-calculator', subtitle: 'Sustainable nest egg & safe withdrawal rate' },
      ],
    },
  ];

  const currentIntentObj = intents.find((i) => i.category === selectedIntent);

  const resetWizard = () => {
    setStep(1);
    setSelectedIntent(null);
    setTargetGoal(null);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      {/* Header */}
      <div className="text-center space-y-3 max-w-2xl mx-auto">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#F3F4F6] text-[#1A1A1A] border border-[#E5E7EB] rounded-full text-xs font-bold">
          <Sparkle className="w-3.5 h-3.5" />
          Financial Tool Discovery Engine
        </span>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#1A1A1A] tracking-tight">
          What are you trying to calculate today?
        </h1>
        <p className="text-sm text-[#6B7280]">
          Not sure which calculator you need? Answer two quick questions to find the exact verified financial tool.
        </p>
      </div>

      {/* Progress Tracker */}
      <div className="flex items-center justify-center gap-2 text-xs font-bold">
        <span className={`px-3 py-1 rounded-full border ${step >= 1 ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]' : 'bg-white text-[#9CA3AF] border-[#E5E7EB]'}`}>
          1. Financial Goal
        </span>
        <span className="text-[#E5E7EB]">→</span>
        <span className={`px-3 py-1 rounded-full border ${step >= 2 ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]' : 'bg-white text-[#9CA3AF] border-[#E5E7EB]'}`}>
          2. Specific Objective
        </span>
        <span className="text-[#E5E7EB]">→</span>
        <span className={`px-3 py-1 rounded-full border ${step === 3 ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]' : 'bg-white text-[#9CA3AF] border-[#E5E7EB]'}`}>
          3. Verified Match
        </span>
      </div>

      {/* STEP 1: Main Category Intent */}
      {step === 1 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-in fade-in duration-200">
          {intents.map((item) => (
            <button
              key={item.category}
              onClick={() => {
                setSelectedIntent(item.category);
                setStep(2);
              }}
              className="p-5 text-left bg-white hover:bg-[#FBFBFC] border border-[#E5E7EB] hover:border-[#1A1A1A] rounded-2xl shadow-xs hover:shadow-sm transition-all space-y-3 group"
            >
              <div className="w-10 h-10 rounded-xl bg-[#F3F4F6] text-[#1A1A1A] flex items-center justify-center group-hover:bg-[#1A1A1A] group-hover:text-white transition-colors">
                {item.icon}
              </div>
              <div>
                <h3 className="text-sm font-bold text-[#1A1A1A] group-hover:text-black">
                  {item.title}
                </h3>
                <p className="text-xs text-[#6B7280] mt-1 leading-relaxed">
                  {item.description}
                </p>
              </div>
              <div className="text-xs font-bold text-[#1A1A1A] flex items-center gap-1 pt-1">
                <span>Select this goal</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* STEP 2: Sub-Goals */}
      {step === 2 && currentIntentObj && (
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 space-y-6 shadow-xs animate-in fade-in duration-200">
          <div className="flex items-center justify-between border-b border-[#E5E7EB] pb-4">
            <div className="flex items-center gap-2">
              <span className="p-2 bg-[#F3F4F6] rounded-lg text-[#1A1A1A]">
                {currentIntentObj.icon}
              </span>
              <div>
                <h2 className="text-base font-bold text-[#1A1A1A]">
                  {currentIntentObj.title}
                </h2>
                <p className="text-xs text-[#6B7280]">
                  Select the precise answer you are looking for:
                </p>
              </div>
            </div>
            <button
              onClick={resetWizard}
              className="text-xs text-[#6B7280] hover:text-[#1A1A1A] flex items-center gap-1 font-semibold"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Change Category</span>
            </button>
          </div>

          <div className="space-y-3">
            {currentIntentObj.goals.map((g, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setTargetGoal(g.toolId);
                  setStep(3);
                }}
                className="w-full p-4 text-left bg-[#FBFBFC] hover:bg-white border border-[#E5E7EB] hover:border-[#1A1A1A] rounded-xl flex items-center justify-between group transition-all"
              >
                <div className="space-y-0.5">
                  <div className="text-sm font-bold text-[#1A1A1A]">
                    {g.label}
                  </div>
                  <div className="text-xs text-[#6B7280]">
                    {g.subtitle}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-[#9CA3AF] group-hover:text-[#1A1A1A] group-hover:translate-x-1 transition-all" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* STEP 3: Matched Tool Ready to Launch */}
      {step === 3 && targetGoal && (() => {
        const matchedTool = tools.find((t) => t.id === targetGoal) || tools[0];
        return (
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 space-y-6 shadow-xs text-center max-w-xl mx-auto animate-in zoom-in-95 duration-200">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-6 h-6" />
            </div>

            <div className="space-y-1">
              <div className="text-xs font-bold text-emerald-700 uppercase tracking-wider">
                Recommended Tool Match
              </div>
              <h2 className="text-xl font-extrabold text-[#1A1A1A]">
                {matchedTool.name}
              </h2>
              <p className="text-xs text-[#6B7280] max-w-md mx-auto">
                {matchedTool.description}
              </p>
            </div>

            <div className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] text-left text-xs space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-bold text-[#1A1A1A]">Methodology Source:</span>
                <span className="text-[#6B7280]">{matchedTool.methodology.source}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-bold text-[#1A1A1A]">Certified For:</span>
                <span className="text-emerald-700 font-semibold">{matchedTool.methodology.jurisdictionDate}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <button
                onClick={() => onSelectTool(matchedTool.id)}
                className="w-full sm:w-auto px-6 py-3 bg-[#1A1A1A] hover:bg-black text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center justify-center gap-2"
              >
                <span>Launch {matchedTool.name}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={resetWizard}
                className="w-full sm:w-auto px-4 py-3 bg-white hover:bg-[#F3F4F6] text-[#6B7280] border border-[#E5E7EB] text-xs font-semibold rounded-xl transition-all"
              >
                Start Over
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
};
